import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Wallet, Map as MapIcon, Loader2, CalendarDays, AlertTriangle, ChevronDown, ChevronUp,
  Truck, User,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import {
  getGiroPreviewZone, reoptimize, createRitiro, getMezzi, setGiroStato, overrideMezzo, updateGiro, api,
  listTrasportatoriAttivi, overrideTrasportatoreGiro,
  createGiroOverride, deleteGiroOverride,
  createGiriFromZonePreview, getGiriOperativiZone, assegnaGiro,
  updateOrdineFermateZone, reoptimizeGiroZone,
  chiudiGiroConRecuperi,
  generaRecuperiDaSaltate,
} from "../lib/api";
import { ChiudiGiroConRecuperiDialog } from "../components/giro/ChiudiGiroConRecuperiDialog";
import {
  todayIsoDate, formatCurrency, formatNumber, STATO_GIRO_LABEL,
} from "../lib/constants";
import { useAuth } from "../contexts/AuthContext";
import { MezzoBlock } from "../components/giro/MezzoBlock";
import { GiroMap } from "../components/giro/GiroMap";
import { RitiroDialog } from "../components/giro/RitiroDialog";
import { PreviewZoneSection } from "../components/giro/PreviewZoneSection";
import { AggiungiExtraDialog } from "../components/giro/AggiungiExtraDialog";
import { openPrintSheet, buildGoogleMapsUrl } from "../components/giro/printSheet";
import { useTutteLeZone } from "../hooks/useZone";

export default function GiroDelGiorno() {
  const { canSeeCommercial, isCarrier } = useAuth();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(searchParams.get("data") || todayIsoDate());
  const [allMezzi, setAllMezzi] = useState([]);
  const [allTrasportatori, setAllTrasportatori] = useState([]);
  const [logo, setLogo] = useState({ text: "Olio Esausto", subtitle: "Gestione ritiri" });
  const [loading, setLoading] = useState(false);
  const [ritiroDialog, setRitiroDialog] = useState(null);
  const [ritiroForm, setRitiroForm] = useState({ kg: "", prezzo: "", note: "" });
  const [doneIds, setDoneIds] = useState(new Set());
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [kmDialog, setKmDialog] = useState(null); // {giroId, stato, label, value}
  // B5.5 — Dialog "Chiudi giro con recuperi"
  const [chiudiDialog, setChiudiDialog] = useState(null); // {giroId, mezzoData, source}
  // Vista giro: "operativo" (Da zone) è ora l'unica modalità principale.
  // "preview" resta disponibile per admin/ufficio come strumento di confronto
  // pre-creazione. Trasportatore vede sempre "operativo".
  const [viewMode, setViewMode] = useState("operativo");
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // BLOCCO B4.1 — anagrafica zone per dedurre i motivi delle differenze
  const { zone: zoneAnagrafica } = useTutteLeZone();
  // BLOCCO B5 — giri operativi persistiti da zone
  const [operativoData, setOperativoData] = useState(null);
  const [operativoLoading, setOperativoLoading] = useState(false);
  const [confirmCreaOperativo, setConfirmCreaOperativo] = useState(false);
  const [creating, setCreating] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null); // {giroId, mezzoId, trasportatoreId}
  // Blocco URGENZA/EXTRA — dialog per aggiungere urgenze
  const [extraDialog, setExtraDialog] = useState(null); // {giro}

  const load = async () => {
    setLoading(true);
    try {
      const [mezziRes, logoRes, traspRes] = await Promise.all([
        getMezzi(),
        api.get("/settings/logo").then((r) => r.data).catch(() => null),
        listTrasportatoriAttivi().catch(() => []),
      ]);
      setAllMezzi(mezziRes);
      setAllTrasportatori(traspRes || []);
      if (logoRes?.logo) setLogo(logoRes.logo);
    } catch {
      toast.error("Errore caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // BLOCCO B4 — Carica preview-zone quando viewMode passa a "preview" o cambia data.
  // Non sostituisce la legacy: la richiesta è indipendente e usata solo per visualizzazione + banner di confronto.
  useEffect(() => {
    if (isCarrier && viewMode === "preview") return; // i trasportatori non vedono preview
    if (viewMode === "operativo") return; // operativo ha il suo useEffect
    if (viewMode !== "preview" && !previewData) return; // carica una sola volta quando serve confronto
    let cancel = false;
    setPreviewLoading(true);
    getGiroPreviewZone(date)
      .then((d) => { if (!cancel) setPreviewData(d); })
      .catch(() => { if (!cancel) setPreviewData(null); })
      .finally(() => { if (!cancel) setPreviewLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, date]);

  // BLOCCO B5 — Carica giri operativi da zone quando viewMode=operativo o cambia data
  useEffect(() => {
    if (viewMode !== "operativo") return;
    let cancel = false;
    setOperativoLoading(true);
    getGiriOperativiZone(date)
      .then((d) => {
        if (!cancel) {
          setOperativoData(d);
          // BLOCCO B5.1 — popola doneIds dai produttori ritirati dei giri operativi
          const newDone = new Set();
          (d?.mezzi || []).forEach((m) => (m.produttoriRitirati || []).forEach((pid) => newDone.add(pid)));
          setDoneIds((prev) => {
            const merged = new Set([...prev, ...newDone]);
            return merged;
          });
        }
      })
      .catch((ex) => {
        if (!cancel) {
          setOperativoData(null);
          toast.error(ex?.response?.data?.detail || "Errore caricamento giri operativi");
        }
      })
      .finally(() => { if (!cancel) setOperativoLoading(false); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, date]);

  const onCreaGiriOperativi = async (force = false) => {
    setCreating(true);
    try {
      const res = await createGiriFromZonePreview(date, force);
      const nCreati = res.creati?.length || 0;
      const nEsist = res.gia_esistenti?.length || 0;
      const totFermate = res.totale_fermate || 0;
      if (nCreati > 0) {
        toast.success(`Creati ${nCreati} giri operativi · ${totFermate} fermate totali${nEsist ? ` · ${nEsist} già esistenti (non duplicati)` : ""}`);
      } else if (nEsist > 0) {
        toast.info(`Nessun nuovo giro: ${nEsist} già esistenti per questa data.`);
      } else if (res.messaggio) {
        toast.warning(res.messaggio);
      } else {
        toast.info("Nessuna zona con fermate per questa data.");
      }
      setConfirmCreaOperativo(false);
      setViewMode("operativo");
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Errore creazione giri");
    } finally {
      setCreating(false);
    }
  };

  const refreshOperativo = async () => {
    try {
      const d = await getGiriOperativiZone(date);
      setOperativoData(d);
      const newDone = new Set();
      (d?.mezzi || []).forEach((m) => (m.produttoriRitirati || []).forEach((pid) => newDone.add(pid)));
      setDoneIds((prev) => new Set([...prev, ...newDone]));
    } catch {
      /* refresh silenziosamente ignorato: la UI mostra "operativo-empty" se serve */
    }
  };

  const onAssegna = async () => {
    if (!assignDialog) return;
    try {
      await assegnaGiro(assignDialog.giroId, {
        mezzoId: assignDialog.mezzoId || "",
        trasportatoreId: assignDialog.trasportatoreId || "",
      });
      toast.success("Assegnazione aggiornata");
      setAssignDialog(null);
      await refreshOperativo();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Errore assegnazione");
    }
  };

  const onChangeDate = (newDate) => {
    setDate(newDate);
    // In modalità operativa: il useEffect su [viewMode, date] ricarica automaticamente
    // In modalità preview: idem
  };

  // B5.5 — Conferma chiusura giro con eventuali recuperi creati lato server
  const confirmChiudiConRecuperi = async ({ kmFinali, recuperi }) => {
    if (!chiudiDialog) return;
    const { giroId } = chiudiDialog;
    try {
      const res = await chiudiGiroConRecuperi(giroId, { kmFinali, recuperi });
      const n = (res?.recuperiCreati || []).length;
      if (n > 0) {
        toast.success(`Giro chiuso · ${n} recuperi creati`);
      } else {
        toast.success("Giro completato");
      }
      await refreshOperativo();
      setChiudiDialog(null);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore chiusura giro");
    }
  };

  const confirmKmAndStato = async () => {
    if (!kmDialog) return;
    const km = Number(kmDialog.value);
    if (!km || km < 0) { toast.error("Inserire un valore km valido"); return; }
    const { giroId, stato } = kmDialog;
    try {
      const field = stato === "IN_CORSO" ? "kmIniziali" : "kmFinali";
      await updateGiro(giroId, { [field]: km });
      await setGiroStato(giroId, stato);
      await refreshOperativo();
      toast.success(`Stato: ${STATO_GIRO_LABEL[stato]} · ${field === "kmIniziali" ? "Km partenza" : "Km arrivo"}: ${km}`);
      setKmDialog(null);
    } catch { toast.error("Errore aggiornamento"); }
  };

  const confirmAnnullamento = async () => {
    const gid = confirmCancel.giroId;
    setConfirmCancel(null);
    try {
      await setGiroStato(gid, "ANNULLATO");
      await refreshOperativo();
      toast.info("Giro annullato");
    } catch { toast.error("Errore"); }
  };

  const openRitiro = (stop, giroId) => {
    const c = stop.contratto || {};
    setRitiroForm({
      kg: "",
      prezzo: c.gratuito ? "0" : (c.prezzoEuroKg != null ? String(c.prezzoEuroKg) : ""),
      note: "",
    });
    setRitiroDialog({ stop, giroId });
  };

  const saveRitiro = async () => {
    if (!ritiroForm.kg) { toast.error("Inserire kg"); return; }
    try {
      const kg = Number(ritiroForm.kg);
      const prezzo = Number(ritiroForm.prezzo) || 0;
      const giroId = ritiroDialog.giroId;
      const mezzoData = (operativoData?.mezzi || []).find((m) => m.giroId === giroId);
      await createRitiro({
        produttoreId: ritiroDialog.stop.id,
        data: new Date().toISOString(),
        quantitaKg: kg,
        prezzoUnitario: prezzo,
        importo: kg * prezzo,
        note: ritiroForm.note,
        mezzoId: mezzoData?.mezzo?.id,
        giroId,
      });
      const next = new Set(doneIds);
      next.add(ritiroDialog.stop.id);
      setDoneIds(next);
      toast.success("Ritiro registrato");
      setRitiroDialog(null);
      await refreshOperativo();
    } catch { toast.error("Errore salvataggio"); }
  };

  const onOpenMaps = (mezzoData) => {
    if (!operativoData) return;
    const { url, included, excluded, depositoExcluded } = buildGoogleMapsUrl(operativoData.deposito, mezzoData.fermate);
    if (!url) {
      toast.error("Nessuna fermata con coordinate o indirizzo valido");
      return;
    }
    window.open(url, "_blank");
    if (excluded.length > 0) {
      const names = excluded.slice(0, 3).map(({ stop, motivo }) =>
        `${stop.ragioneSociale || stop.id?.slice(0,8) || "?"} (${stop.indirizzo || "?"}) — ${motivo}`
      ).join("\n");
      const more = excluded.length > 3 ? `\n…e altre ${excluded.length - 3}` : "";
      toast.warning(
        `Google Maps aperto con ${included.length} fermate. ${excluded.length} esclusa/e perché l'indirizzo è da verificare.\n\n${names}${more}`,
        { duration: 10000 }
      );
    }
    if (depositoExcluded) {
      toast.warning(
        `Deposito non incluso nel percorso: ${depositoExcluded}. Configurare l'indirizzo del deposito per migliorare il giro.`,
        { duration: 10000 }
      );
    }
  };

  const onPrint = (mezzoData) => {
    // Unico flusso: source="zone"
    const src = operativoData;
    if (!src) return;
    const ok = openPrintSheet({
      data: src.data,
      giorno: src.giorno,
      settimana: src.settimana,
      mezzoData,
      logo,
      source: "zone",
    });
    if (!ok) toast.error("Popup bloccato dal browser");
  };

  // Push 5: Override su fermata (Sposta mezzo / Sposta giorno / Salta oggi / Aggiungi extra / Annulla)
  // Ora esclusivo per la vista operativa (source="zone"). Nessun fallback legacy.
  const handleStopOverride = async (action, payload) => {
    try {
      if (action === "cancel") {
        await deleteGiroOverride(payload.overrideId);
        toast.success("Override annullato");
      } else {
        await createGiroOverride(payload);
        toast.success("Override applicato");
      }
      await refreshOperativo();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore override");
    }
  };

  // BLOCCO B5.1 — skip/restore per giri operativi zone-based
  const onSkipStopOperativo = async (giroId, stop, salta) => {
    try {
      await api.put(`/giri/${giroId}/skip`, { produttoreId: stop.id, salta });
      await refreshOperativo();
      toast.success(salta ? "Fermata saltata" : "Fermata ripristinata");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore");
    }
  };

  // BLOCCO B5.2 — Drag&drop persistente sui giri operativi zone
  // Stesso pattern del legacy: aggiorna prima lo state (ottimistico) poi POSTA al server.
  const handleReorderOperativo = async (giroId, fascia, newOrder) => {
    if (!operativoData) return;
    const mezzoData = (operativoData.mezzi || []).find((m) => m.giroId === giroId);
    if (!mezzoData) return;
    const updatedFasce = (mezzoData.fasce || []).map((f) =>
      f.fascia === fascia ? { ...f, stops: newOrder } : f,
    );
    const activeFlat = updatedFasce.flatMap((f) => f.stops);
    // Saltati restano nell'ordine relativo originale: vengono mantenuti come coda
    const saltatiOriginali = (mezzoData.produttoriSaltati || []);
    const fullOrder = [...activeFlat, ...saltatiOriginali];
    const stopsById = Object.fromEntries(mezzoData.fermate.map((s) => [s.id, s]));
    const newFermate = activeFlat.map((id) => stopsById[id]).filter(Boolean);
    setOperativoData({
      ...operativoData,
      mezzi: operativoData.mezzi.map((m) =>
        m.giroId === giroId ? { ...m, fasce: updatedFasce, fermate: newFermate } : m,
      ),
    });
    try {
      await updateOrdineFermateZone(giroId, fullOrder);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Ordine non salvato sul server");
      await refreshOperativo();
    }
  };

  // BLOCCO B5.2 — Re-optimize TSP runtime sui giri operativi zone
  const handleReoptimizeOperativo = async (giroId) => {
    if (!operativoData) return;
    const mezzoData = (operativoData.mezzi || []).find((m) => m.giroId === giroId);
    if (!mezzoData) return;
    if (!window.confirm("Vuoi rigenerare il percorso? Le modifiche manuali andranno perse.")) return;
    try {
      await reoptimizeGiroZone(giroId);
      await refreshOperativo();
      toast.success("Percorso ricalcolato");
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore re-optimize");
    }
  };

  // BLOCCO B5.1 — change stato giro operativo (riusa logica km-dialog/annulla)
  // Nota: handleChangeStato attuale aggiorna `data.mezzi` (legacy). Per operativo
  // basta ricaricare dopo il cambio. Wrappiamo con refresh selettivo.
  const handleChangeStatoOperativo = async (giroId, newStato) => {
    if (newStato === "ANNULLATO") {
      setConfirmCancel({ giroId, source: "zone" });
      return;
    }
    if (newStato === "IN_CORSO") {
      setKmDialog({ giroId, stato: newStato, label: "Km di partenza", value: "", source: "zone" });
      return;
    }
    if (newStato === "COMPLETATO") {
      // B5.5 — Apri dialog "Chiudi giro con recuperi"
      const mezzoData = (operativoData?.mezzi || []).find((m) => m.giroId === giroId);
      setChiudiDialog({ giroId, mezzoData, source: "zone" });
      return;
    }
    try {
      await setGiroStato(giroId, newStato);
      await refreshOperativo();
      toast.success(`Stato: ${STATO_GIRO_LABEL[newStato]}`);
    } catch { toast.error("Errore aggiornamento stato"); }
  };

  // Riparazione stato incoerente: giri già COMPLETATO/ANNULLATO che hanno
  // fermate saltate senza recupero. Solo admin/ufficio. NO patch massiva.
  const handleCreaRecuperiDaSaltate = async (giroId) => {
    if (!window.confirm(
      "Creare recuperi APERTO per tutte le fermate saltate di questo giro chiuso?\n\n" +
      "Ogni fermata saltata non ancora coperta da un recupero verrà " +
      "registrata in /recuperi con motivo 'Da riprogrammare'."
    )) return;
    try {
      const res = await generaRecuperiDaSaltate(giroId);
      const n = res?.creati || 0;
      if (n > 0) {
        toast.success(`${n} recuperi creati da fermate saltate`);
      } else {
        toast.info("Nessun recupero da creare (già coperti).");
      }
      await refreshOperativo();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore creazione recuperi");
    }
  };


  return (
    <div className="space-y-4" data-testid="giro-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Giro del giorno</h1>
        <p className="text-slate-500 text-sm mt-1">Pianificazione e tracciamento giornaliero</p>
      </div>

      <Card className="p-4 bg-white border border-slate-200 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-xs">Data</Label>
            <Input type="date" value={date} onChange={(e) => onChangeDate(e.target.value)} data-testid="date-input" />
          </div>
          {operativoData && (
            <div className="text-xs text-slate-500" data-testid="giro-data-info">
              <span className="font-semibold">{operativoData.data}</span> · {(operativoData.mezzi || []).length} giri operativi
            </div>
          )}
          {!isCarrier && (
            <div className="ml-auto flex items-center gap-1" data-testid="view-mode-toggle">
              <button
                type="button"
                onClick={() => setViewMode("preview")}
                className={`h-9 px-3 rounded-l-md text-xs font-medium border transition-colors ${
                  viewMode === "preview"
                    ? "bg-[#1565C0] text-white border-[#1565C0]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#1565C0]/40"
                }`}
                data-testid="view-mode-preview"
              >
                Preview da zone
              </button>
              <button
                type="button"
                onClick={() => setViewMode("operativo")}
                className={`h-9 px-3 rounded-r-md text-xs font-medium border -ml-px transition-colors ${
                  viewMode === "operativo"
                    ? "bg-[#7B1FA2] text-white border-[#7B1FA2]"
                    : "bg-white text-slate-700 border-slate-200 hover:border-[#7B1FA2]/40"
                }`}
                data-testid="view-mode-operativo"
              >
                Giro operativo da zone
              </button>
            </div>
          )}
        </div>
      </Card>

      {/* BLOCCO B4 — Sezione PREVIEW (rendering alternativo, admin/ufficio) */}
      {viewMode === "preview" && !isCarrier && (
        <>
          <PreviewZoneSection previewData={previewData} loading={previewLoading} isCarrier={isCarrier} />
          {/* BLOCCO B5 — Pulsante crea giri operativi (admin/ufficio, dentro preview) */}
          {previewData && previewData.zone && previewData.zone.length > 0 && (
            <Card className="p-3 bg-[#7B1FA2]/5 border border-[#7B1FA2]/30">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">Pronto per creare i giri operativi?</div>
                  <div className="text-xs text-slate-600">
                    Verranno creati {previewData.zone.filter(z => z.fermate?.length > 0).length} giri persistenti
                    {" "}(source=&quot;zone&quot;) · {previewData.totale_produttori} fermate totali. La modalità legacy non verrà toccata.
                  </div>
                </div>
                <Button
                  className="bg-[#7B1FA2] hover:bg-[#6A1B9A] text-white"
                  onClick={() => setConfirmCreaOperativo(true)}
                  data-testid="btn-crea-operativi"
                >
                  Crea giri operativi da questa preview
                </Button>
              </div>
            </Card>
          )}
          {/* Confronto dettagliato Legacy vs Preview rimosso con il modello Legacy */}
        </>
      )}

      {/* BLOCCO B5 — Vista giri operativi persistiti da zone (admin/ufficio + trasportatore assegnato) */}
      {viewMode === "operativo" && (
        operativoLoading ? (
          <Card className="p-8 text-center text-slate-500" data-testid="operativo-loading">
            <span className="text-sm">Caricamento giri operativi…</span>
          </Card>
        ) : !operativoData || (operativoData.mezzi || []).length === 0 ? (
          <Card className="p-4 bg-[#7B1FA2]/10 border border-[#7B1FA2]/30" data-testid="operativo-empty">
            <div className="text-sm font-semibold text-[#7B1FA2]">
              {operativoData?.messaggio || "Nessun giro operativo da zone per questa data."}
            </div>
            {!isCarrier && (
              <div className="text-xs text-slate-600 mt-1">
                Vai su &quot;Preview da zone&quot; per generarli.
              </div>
            )}
          </Card>
        ) : (
          <div className="space-y-3" data-testid="operativo-section">
            {/* Blocco URGENZA/EXTRA — Riepilogo carico lavoro (admin/ufficio) */}
            {!isCarrier && operativoData.caricoLavoro && operativoData.mezzi.length > 0 && (
              <Card
                className="p-3 bg-white border border-slate-200"
                data-testid="carico-lavoro-card"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-700">
                    Carico lavoro del giorno
                  </div>
                  {operativoData.caricoLavoro.extra > 0 && (
                    <Badge
                      className="bg-[#F57C00] text-white text-[10px]"
                      data-testid="carico-extra-totali-badge"
                    >
                      {operativoData.caricoLavoro.extra} urgenze
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">
                      Fermate totali
                    </div>
                    <div
                      className="text-xl font-bold text-slate-900"
                      data-testid="carico-totali"
                    >
                      {operativoData.caricoLavoro.totaleFermate}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">
                      Ordinarie
                    </div>
                    <div
                      className="text-xl font-bold text-[#1B5E20]"
                      data-testid="carico-ordinarie"
                    >
                      {operativoData.caricoLavoro.ordinarie}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">
                      Extra / Urgenze
                    </div>
                    <div
                      className="text-xl font-bold text-[#F57C00]"
                      data-testid="carico-extra"
                    >
                      {operativoData.caricoLavoro.extra}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    {operativoData.caricoLavoro.giriSenzaMezzo > 0 && (
                      <div
                        className="text-[11px] text-[#C62828]"
                        data-testid="carico-senza-mezzo"
                      >
                        ⚠ {operativoData.caricoLavoro.giriSenzaMezzo} giro/i senza mezzo
                      </div>
                    )}
                    {operativoData.caricoLavoro.giriSenzaAutista > 0 && (
                      <div
                        className="text-[11px] text-[#C62828]"
                        data-testid="carico-senza-autista"
                      >
                        ⚠ {operativoData.caricoLavoro.giriSenzaAutista} giro/i senza autista
                      </div>
                    )}
                    {operativoData.caricoLavoro.extraNonAssegnate > 0 && (
                      <div
                        className="text-[11px] text-[#F57C00]"
                        data-testid="carico-extra-non-assegnate"
                      >
                        ⚠ {operativoData.caricoLavoro.extraNonAssegnate} urgenze non assegnate
                      </div>
                    )}
                  </div>
                </div>
                {/* Breakdown per autista (se ci sono extra) */}
                {operativoData.caricoLavoro.extra > 0 &&
                  operativoData.caricoLavoro.perAutista.length > 0 && (
                    <div
                      className="mt-3 pt-2 border-t border-slate-100"
                      data-testid="carico-per-autista"
                    >
                      <div className="text-[10px] uppercase text-slate-500 mb-1">
                        Per autista
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {operativoData.caricoLavoro.perAutista.map((a) => (
                          <div
                            key={a.id}
                            className="text-[11px] px-2 py-1 rounded-md bg-slate-50 border border-slate-200"
                            data-testid={`carico-autista-${a.id}`}
                          >
                            <span className="font-semibold">
                              {a.nome} {a.cognome || ""}
                            </span>{" "}
                            <span className="text-slate-500">
                              {a.ordinarie} ord
                            </span>
                            {a.extra > 0 && (
                              <span className="text-[#F57C00] font-semibold">
                                {" "}
                                · +{a.extra} extra
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </Card>
            )}

            <GiroMap data={operativoData} />
            {(operativoData.mezzi || []).map((m) => (
              <div key={m.giroId} data-testid={`operativo-giro-${m.giroId}`}>
                <Card className="p-3 mb-2 bg-white border border-[#7B1FA2]/20">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-[#7B1FA2] text-white text-[10px]">DA ZONE</Badge>
                    <div className="font-semibold text-slate-900">{m.zona}</div>
                    {m.giornoServizioZona && (
                      <Badge variant="outline" className="text-[10px]">{m.giornoServizioZona}</Badge>
                    )}
                    <div className="flex items-center gap-1 text-xs">
                      <Truck size={12} className="text-slate-400" />
                      <span
                        className={!m.mezzo?.id ? "text-[#F57C00] italic" : ""}
                        data-testid={`op-mezzo-${m.giroId}`}
                      >
                        {m.mezzo?.id ? m.mezzo.nome : "— Da assegnare —"}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                      <User size={12} className="text-slate-400" />
                      <span
                        className={!m.trasportatore ? "text-[#F57C00] italic" : ""}
                        data-testid={`op-trasp-${m.giroId}`}
                      >
                        {m.trasportatore
                          ? `${m.trasportatore.nome} ${m.trasportatore.cognome || ""}`.trim()
                          : "— Da assegnare —"}
                      </span>
                    </div>
                    {!isCarrier && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs ml-auto border-[#F57C00] text-[#E65100] hover:bg-[#F57C00]/10"
                          onClick={() => setExtraDialog({ giro: m })}
                          data-testid={`op-aggiungi-extra-btn-${m.giroId}`}
                          disabled={m.stato === "COMPLETATO" || m.stato === "ANNULLATO"}
                          title={
                            (m.stato === "COMPLETATO" || m.stato === "ANNULLATO")
                              ? "Giro chiuso: usa un recupero"
                              : "Aggiungi una fermata urgente / extra"
                          }
                        >
                          <AlertTriangle size={12} className="mr-1" />
                          Aggiungi urgenza / extra
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => setAssignDialog({
                            giroId: m.giroId,
                            mezzoId: m.mezzo?.id || "",
                            trasportatoreId: m.trasportatore?.id || "",
                          })}
                          data-testid={`op-assegna-btn-${m.giroId}`}
                        >
                          Assegna mezzo/trasportatore
                        </Button>
                      </>
                    )}
                  </div>
                  {/* Riepilogo carico per giro */}
                  {(m.nFermateExtra > 0 || m.nFermateOrdinarie > 0) && (
                    <div
                      className="flex items-center gap-2 mt-2 pt-2 border-t border-[#7B1FA2]/10 text-[11px]"
                      data-testid={`op-carico-giro-${m.giroId}`}
                    >
                      <span className="text-slate-500">Fermate:</span>
                      <Badge variant="outline" className="text-[10px]" data-testid={`op-ordinarie-${m.giroId}`}>
                        Ordinarie {m.nFermateOrdinarie || 0}
                      </Badge>
                      {m.nFermateExtra > 0 && (
                        <Badge className="bg-[#F57C00] text-white text-[10px]" data-testid={`op-extra-${m.giroId}`}>
                          Extra {m.nFermateExtra}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]" data-testid={`op-totali-${m.giroId}`}>
                        Totale {m.nFermateTotali || 0}
                      </Badge>
                    </div>
                  )}
                </Card>
                <MezzoBlock
                  mezzoData={m}
                  doneIds={doneIds}
                  onReorder={handleReorderOperativo}
                  onReoptimize={handleReoptimizeOperativo}
                  onRegister={(stop) => openRitiro(stop, m.giroId)}
                  onChangeStato={handleChangeStatoOperativo}
                  onChangeMezzo={() => {}}
                  onChangeTrasportatore={() => {}}
                  onOpenMaps={onOpenMaps}
                  onPrint={onPrint}
                  onSkip={(stop) => onSkipStopOperativo(m.giroId, stop, true)}
                  onRestore={(stop) => onSkipStopOperativo(m.giroId, stop, false)}
                  onStopOverride={handleStopOverride}
                  onCreaRecuperoSaltata={() => handleCreaRecuperiDaSaltate(m.giroId)}
                  allMezzi={allMezzi}
                  allTrasportatori={allTrasportatori}
                  isCarrier={isCarrier}
                  disableReorder={isCarrier}
                  hideInlineAssegnazione={true}
                  dataGiro={operativoData?.data || date}
                />
              </div>
            ))}
          </div>
        )
      )}

      {/* Resto della pagina (legacy operativo) viene nascosto in modalità preview */}
      {/* Ramo viewMode==='legacy' rimosso: unico flusso operativo è 'operativo' (zone) */}


      <RitiroDialog
        open={!!ritiroDialog}
        onClose={() => setRitiroDialog(null)}
        stop={ritiroDialog?.stop}
        form={ritiroForm}
        setForm={setRitiroForm}
        onSave={saveRitiro}
        hidePrice={isCarrier}
        showPiva={isCarrier}
      />

      <Dialog open={!!kmDialog} onOpenChange={(v) => !v && setKmDialog(null)}>
        <DialogContent data-testid="km-dialog">
          <DialogHeader>
            <DialogTitle>{kmDialog?.label}</DialogTitle>
            <DialogDescription>
              Inserisci il valore di chilometri letto sul contachilometri del mezzo.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider">Chilometraggio</Label>
            <Input
              type="number" inputMode="numeric" autoFocus
              value={kmDialog?.value || ""}
              onChange={(e) => setKmDialog({ ...kmDialog, value: e.target.value })}
              data-testid="km-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKmDialog(null)}>Annulla</Button>
            <Button onClick={confirmKmAndStato} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="km-confirm-btn">
              Salva e {kmDialog?.stato === "IN_CORSO" ? "inizia giro" : "termina giro"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmCancel} onOpenChange={(v) => !v && setConfirmCancel(null)}>
        <DialogContent data-testid="confirm-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Annullare il giro?</DialogTitle>
            <DialogDescription>Il giro non sarà più visibile nella Dashboard di oggi e nessun ritiro potrà essere registrato. Puoi sempre riaprirlo cambiando lo stato.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCancel(null)}>No, mantieni</Button>
            <Button onClick={confirmAnnullamento} className="bg-[#C62828] hover:bg-[#B71C1C] text-white" data-testid="confirm-cancel-btn">Sì, annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BLOCCO B5 — Conferma creazione giri operativi da preview */}
      <Dialog open={confirmCreaOperativo} onOpenChange={(v) => !v && setConfirmCreaOperativo(false)}>
        <DialogContent data-testid="dialog-conferma-crea-operativi">
          <DialogHeader>
            <DialogTitle>Crea giri operativi da zone</DialogTitle>
            <DialogDescription>
              {previewData ? (
                <>
                  Data: <strong>{previewData.data}</strong> · {previewData.giorno} · {previewData.settimana}<br />
                  Zone con fermate: <strong>{(previewData.zone || []).filter(z => z.fermate?.length > 0).length}</strong><br />
                  Produttori dovuti: <strong>{previewData.totale_produttori}</strong><br /><br />
                  Verranno creati i giri persistenti (<code>source=&quot;zone&quot;</code>) per le zone con fermate.
                  Il giro legacy <strong>non verrà modificato</strong>. Operazione idempotente: i giri già esistenti
                  per la stessa data/zona non verranno duplicati.
                </>
              ) : "Caricamento preview…"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmCreaOperativo(false)} disabled={creating}>Annulla</Button>
            <Button
              onClick={() => onCreaGiriOperativi(false)}
              disabled={creating}
              className="bg-[#7B1FA2] hover:bg-[#6A1B9A] text-white"
              data-testid="confirm-crea-operativi-btn"
            >
              {creating ? "Creazione…" : "Crea giri operativi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BLOCCO B5.5 — Dialog chiusura giro con eventuali recuperi */}
      {chiudiDialog && (
        <ChiudiGiroConRecuperiDialog
          open={!!chiudiDialog}
          onClose={() => setChiudiDialog(null)}
          mezzoData={chiudiDialog.mezzoData}
          doneIds={doneIds}
          defaultKmFinali={chiudiDialog.mezzoData?.kmFinali ?? ""}
          onConfirm={confirmChiudiConRecuperi}
        />
      )}

      {/* BLOCCO B5 — Dialog assegnazione mezzo/trasportatore */}
      <Dialog open={!!assignDialog} onOpenChange={(v) => !v && setAssignDialog(null)}>
        <DialogContent data-testid="dialog-assegna-giro">
          <DialogHeader>
            <DialogTitle>Assegna mezzo e trasportatore</DialogTitle>
            <DialogDescription>Aggiorna l&apos;assegnazione del giro operativo. Lascia vuoto per rimuovere.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Mezzo</Label>
              <select
                value={assignDialog?.mezzoId || ""}
                onChange={(e) => setAssignDialog({ ...assignDialog, mezzoId: e.target.value })}
                className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm"
                data-testid="assign-mezzo-select"
              >
                <option value="">— Nessuno —</option>
                {(allMezzi || []).filter(m => m.attivo).map((m) => (
                  <option key={m.id} value={m.id}>{m.nome} {m.targa ? `(${m.targa})` : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Trasportatore</Label>
              <select
                value={assignDialog?.trasportatoreId || ""}
                onChange={(e) => setAssignDialog({ ...assignDialog, trasportatoreId: e.target.value })}
                className="w-full h-10 border border-slate-200 rounded-md px-2 text-sm"
                data-testid="assign-trasp-select"
              >
                <option value="">— Nessuno —</option>
                {(allTrasportatori || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.nome} {t.cognome || ""}</option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog(null)}>Annulla</Button>
            <Button onClick={onAssegna} className="bg-[#1B5E20] hover:bg-[#144818] text-white" data-testid="assign-confirm-btn">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Blocco URGENZA/EXTRA — Dialog per aggiungere fermata extra/urgenza al giro */}
      {extraDialog?.giro && (
        <AggiungiExtraDialog
          open={!!extraDialog}
          onClose={() => setExtraDialog(null)}
          giro={extraDialog.giro}
          dataGiro={operativoData?.data || date}
          onSuccess={() => refreshOperativo()}
        />
      )}
    </div>
  );
}
